import * as React from 'react';
import {Query} from 'react-apollo';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetSyncStatuses as GetSyncStatusesQuery,
    UserEmail as UserEmailFragment,
} from 'nutshell-graphql-types';

import GET_SYNC_STATUSES_QUERY from '../../integrations/graphql/queries/get-sync-statuses.graphql';

const DEFAULT_POLL_TIMEOUT_MILLISECONDS = 120000; // two minutes
const DEFAULT_POLL_INTERVAL_MILLISECONDS = 2000; // Note, it seems like this actually gets doubled, not sure why.
const POLLING_NETWORK_STATUS = 6;
class EmailSyncQuery extends Query<GetSyncStatusesQuery, Record<any, any>> {}

type Props = {
    onVerificationPollTimeout?: () => void;
    pollTimeout: number;
    pollInterval: number;
    children: (arg1: {
        configuredUserEmails: UserEmailFragment[];
        emailAddresses: UserEmailFragment[];
        refetch: () => Promise<any>;
        isLoading: boolean;
        errorMessage: string | null | undefined;
    }) => React.ReactElement;
};

GetEmailSendingSettings.defaultProps = {
    pollTimeout: DEFAULT_POLL_TIMEOUT_MILLISECONDS,
    pollInterval: DEFAULT_POLL_INTERVAL_MILLISECONDS,
};

export function GetEmailSendingSettings(props: Props) {
    const [pollStarted, setPollStarted] = React.useState<boolean>(false);

    return (
        <EmailSyncQuery
            query={GET_SYNC_STATUSES_QUERY}
            // @ts-expect-error
            fetchPolicy='cache-and-network'
            notifyOnNetworkStatusChange={true}
        >
            {({data, loading, refetch, error, startPolling, stopPolling, networkStatus}) => {
                const loggedInUser = data && data.loggedInUser;
                const emailAddresses = loggedInUser ? loggedInUser.emailAddresses : [];
                const configuredUserEmails = removeUnconfiguredEmails(emailAddresses);
                // @ts-expect-error
                const errorMessage = safelyGetGraphQLErrorMessage(error);

                function refetchEmails() {
                    setPollStarted(false);

                    return refetch();
                }

                // If any of the current email addresses are in the "verifying"
                const hasUnverifiedEmails = getVerifyingSmtpEmails(emailAddresses).length;
                // Poll every second until we hit our timeout to see if we get an update.
                if (hasUnverifiedEmails && !pollStarted) {
                    startPolling(props.pollInterval);
                    setPollStarted(true);

                    // Sane timeout to bail here.
                    setTimeout(() => {
                        if (props.onVerificationPollTimeout) {
                            props.onVerificationPollTimeout();
                        }
                        stopPolling();
                    }, props.pollTimeout);

                    // If data has changed underneath us, stop polling.
                } else if (!hasUnverifiedEmails) {
                    stopPolling();
                }

                return props.children({
                    isLoading: loading && networkStatus !== POLLING_NETWORK_STATUS,
                    errorMessage,
                    refetch: refetchEmails,
                    emailAddresses,
                    configuredUserEmails,
                });
            }}
        </EmailSyncQuery>
    );
}

function removeUnconfiguredEmails(emailAddresses: UserEmailFragment[]) {
    return emailAddresses.filter((address) => address.type !== 'UNCONFIGURED');
}

/**
 * Checks all user emails for any SMTP-configured email addresses that
 * are currently in an "VERIFYING" state. (Technically "UNVERIFIED") from
 * the server.
 *
 * @param  array emailAddresses - Array of user email address objects to filter through
 * @return array                - Array of email addresses still verifying
 */
function getVerifyingSmtpEmails(emailAddresses: UserEmailFragment[]) {
    return emailAddresses.filter(
        (address) =>
            address.type === 'SMTP' &&
            address.smtpCredentials &&
            address.smtpCredentials.verificationStatus === 'UNVERIFIED'
    );
}
